import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
  Divider,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  Language as LanguageIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  Science as ScienceIcon,
  Biotech as BiotechIcon
} from '@mui/icons-material';

interface ScrapedContentModalProps {
  open: boolean;
  onClose: () => void;
  url?: string;
  scrapedContent?: string;
  extractedInfo?: {
    companyName?: string;
    industry?: string;
    focusAreas?: string[];
    values?: string[];
    keywords?: string[];
    therapeuticArea?: string;
    therapeuticAreas?: string[];
    modality?: string;
    modalities?: string[];
    crawledAt?: Date;
  };
}

const ScrapedContentModal: React.FC<ScrapedContentModalProps> = ({
  open,
  onClose,
  url,
  scrapedContent,
  extractedInfo
}) => {
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  const formatContent = (content: string) => {
    // Clean up the content for better readability
    // Remove excessive whitespace and empty lines
    return content
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .join('\n\n');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LanguageIcon color="primary" />
          <Typography variant="h6">Scraped Content Preview</Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ ml: 2 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* URL and Timestamp */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Source URL
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-all' }}>
            {url || 'N/A'}
          </Typography>
          
          {extractedInfo?.crawledAt && (
            <Chip
              icon={<ScheduleIcon />}
              label={`Crawled: ${formatDate(extractedInfo.crawledAt)}`}
              size="small"
              variant="outlined"
              sx={{ mt: 1 }}
            />
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Extracted Information */}
        {extractedInfo && (
          <>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon fontSize="small" />
              Extracted Information
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'rgba(79, 172, 254, 0.02)' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Company Name
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {extractedInfo.companyName || 'Not available'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ScienceIcon fontSize="small" />
                    Therapeutic Area
                  </Typography>
                  <Typography variant="body1">
                    {extractedInfo.therapeuticArea || extractedInfo.therapeuticAreas?.join(', ') || 'Not available'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BiotechIcon fontSize="small" />
                    Modality
                  </Typography>
                  <Typography variant="body1">
                    {extractedInfo.modality || extractedInfo.modalities?.join(', ') || 'Not available'}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </>
        )}

        {/* Raw Content */}
        <Typography variant="h6" gutterBottom>
          Raw Content
        </Typography>
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 2, 
            maxHeight: '400px', 
            overflow: 'auto',
            bgcolor: 'grey.50',
            '& pre': {
              margin: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              lineHeight: 1.5
            }
          }}
        >
          {scrapedContent ? (
            <pre>{formatContent(scrapedContent)}</pre>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center">
              No content available
            </Typography>
          )}
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScrapedContentModal;